import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';
import { normalizeDailyZoomRadius, resolveDailyZoomOrigin } from './games/dailyZoomConfig';

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/daily-puzzle`;
};

const buildPublicHeaders = () => {
  const headers = {};

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt
    ? jwtAnonRaw
    : anonLooksJwt
      ? anonRaw
      : null;

  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }

  return headers;
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { data, text };
};

const normalizePuzzle = (raw) => {
  if (!raw) return null;
  return {
    dateUtc: String(raw.dateUtc || ''),
    word: String(raw.word || '').toLowerCase(),
    imageUrl: String(raw.imageUrl || ''),
    riddleText: String(raw.riddleText || raw.riddle_text || ''),
    source: String(raw.source || ''),
    status: String(raw.status || ''),
    zoomOrigin: resolveDailyZoomOrigin(raw),
    zoomRadius: normalizeDailyZoomRadius(raw?.zoomRadius ?? raw?.zoom_radius),
    nextRolloverAtUtc: raw?.nextRolloverAtUtc || null,
    secondsUntilRollover: Number.isFinite(Number(raw?.secondsUntilRollover))
      ? Number(raw.secondsUntilRollover)
      : null,
    window: Array.isArray(raw?.window)
      ? raw.window.map((row) =>
          row
            ? {
                id: row.id || null,
                dateUtc: String(row.dateUtc || ''),
                word: String(row.word || '').toLowerCase(),
                imageUrl: String(row.imageUrl || ''),
                riddleText: String(row.riddleText || row.riddle_text || ''),
                source: String(row.source || ''),
                status: String(row.status || ''),
                zoomOrigin: resolveDailyZoomOrigin(row),
                zoomRadius: normalizeDailyZoomRadius(row?.zoomRadius ?? row?.zoom_radius),
              }
            : null,
        )
      : null,
  };
};

export const fetchDailyPuzzle = async ({ days = 1 } = {}) => {
  const normalizedDays = Math.min(Math.max(Math.floor(Number(days) || 1), 1), 14);
  const headers = buildPublicHeaders();

  const response = await fetch(`${getFunctionsUrl()}?days=${normalizedDays}`, {
    method: 'GET',
    headers,
  });

  const { data, text } = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(data?.error || text || 'Failed to load daily puzzle.');
    error.code = data?.code;
    error.status = response.status;
    throw error;
  }

  return normalizePuzzle(data);
};

